import React, { useCallback, useEffect, useRef, useState } from "react";
import { WavRecorder, WavStreamPlayer } from "wavtools";
import { io, Socket } from "socket.io-client";
import { useAppStore } from "../stores/appStore";
import { NoiseAnalyzer, rmsAndDbfsInt16 } from "./noiseAnalysis";
import { VoiceBargeInDetector } from "./voiceBargeInDetector";
import { stopAllVisualizerMicStreams } from "../components/live-speech-stage/wavesurfer-visualizers/wavesurfer-visualizers";
import {
  END_OF_UTTERANCE_SILENCE_MS,
  scheduleTrailingSilenceChunks,
  type TrailingSilenceCancel,
} from "./trailingSilence";
import {
  AI_DIALOGUE_START_TIMEOUT_MS,
  mapResponseError,
  OFFLINE_SESSION_ERROR,
  isBrowserOffline,
  type LiveSpeechSessionError,
} from "../types/liveSpeechSessionError";
import { getAuthToken, syncAuthCookie } from "./authToken";
import { emitBestEffortInterrupt } from "./realtimeInterrupt";

const SAMPLES_PER_SUBCHUNK = 480;
const MAX_QUEUE_SIZE = 5;
const SILENT_RECONNECT_TIMEOUT_MS = 5_000;

const CONNECTION_LOST_SESSION_ERROR: LiveSpeechSessionError = {
  code: "disconnect",
  message: "Connection lost. You can retry or end the session.",
  retryable: true,
  canFallbackAudioOnly: false,
};

interface LiveSpeechSocketParams {
  user_id: string;
  auth: {
    [key: string]: any;
  };
  additionalMessageProps: {
    [key: string]: any;
  };
  isMuted: boolean;
  setIsMuted: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
  triggerMicrophoneTip: () => void;
  onMicrophoneError?: () => void;
  onTurnCompleted?: () => void;
  shouldStopAI?: boolean;
  onSocketConnected?: () => void;
  onSessionError?: (error: LiveSpeechSessionError) => void;
  isLessonDialogue?: boolean;
  isTabVisible?: boolean;
  // Optional hooks for visualizing audio activity (waveforms/meters)
  onUserPcmChunk?: (chunk: Int16Array) => void;
  onAiPcmChunk?: (chunk: Int16Array) => void;

  onUserBargeIn?: () => void;

  onAiPlaybackStateChange?: (isPlaying: boolean) => void;

  onUserSpeakingStateChange?: (isSpeaking: boolean) => void;

  onNoiseWarning?: (result: {
    shouldWarn: boolean;
    message: string | null;
  }) => void;
  isPushToTalk?: boolean;
}

const isSilentSubChunkFixed = (
  chunk: Int16Array,
  silenceThresholdDbfs: number,
): boolean => {
  if (!chunk || chunk.length < 1) return true;
  const { dbfs } = rmsAndDbfsInt16(chunk);
  return dbfs < silenceThresholdDbfs;
};

export const useLiveSpeechSocket = ({
  user_id,
  auth,
  additionalMessageProps,
  isMuted,
  setIsMuted,
  onClose,
  triggerMicrophoneTip,
  onMicrophoneError,
  onTurnCompleted,
  shouldStopAI = false,
  onSocketConnected,
  onSessionError,
  isLessonDialogue = false,
  isTabVisible = true,
  onUserPcmChunk,
  onAiPcmChunk,
  onUserBargeIn,
  onAiPlaybackStateChange,
  onUserSpeakingStateChange,
  onNoiseWarning,
  isPushToTalk = false,
}: LiveSpeechSocketParams) => {
  const isMutedRef = useRef(isMuted);
  const isCurrentlySendingRef = useRef(false);
  const socketRef = useRef<Socket | null>(null);
  const recorderRef = useRef<WavRecorder | null>(null);
  const playerRef = useRef<WavStreamPlayer | null>(null);
  const [micMediaStream, setMicMediaStream] = useState<MediaStream | null>(
    null,
  );
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chunkQueue = useRef<Int16Array[]>([]);
  const setIsMutedRef = useRef(setIsMuted);
  const onSocketConnectedRef = useRef(onSocketConnected);
  onSocketConnectedRef.current = onSocketConnected;
  const additionalMessagePropsRef = useRef(additionalMessageProps);
  additionalMessagePropsRef.current = additionalMessageProps;

  const userHasSpokenRef = useRef(false);
  const aiIsSpeakingRef = useRef(false);
  const aiSilenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldStopAIRef = useRef(shouldStopAI);
  const isProcessingMessageEndRef = useRef(false);
  // Guards against races where stopSpeeking() happens during initSpeeking() / begin()
  const stopRequestedRef = useRef(false);
  const initSeqRef = useRef(0);
  const aiStartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intentionalDisconnectRef = useRef(false);
  const sessionErrorActiveRef = useRef(false);
  const silentReconnectInFlightRef = useRef(false);
  const lastSessionErrorCodeRef = useRef<LiveSpeechSessionError["code"] | null>(
    null,
  );
  const onSessionErrorRef = useRef(onSessionError);
  const onCloseRef = useRef(onClose);
  const isTabVisibleRef = useRef(isTabVisible);
  const hasAIDialogueStartedRef = useRef(false);
  const [isMicReady, setIsMicReady] = useState(false);
  const initialConnectInFlightRef = useRef(false);
  const lastUserAudioAtRef = useRef<number>(0);
  const shouldPauseAIPlaybackRef = useRef(false);
  const bargeInCooldownUntilRef = useRef<number>(0);
  const lastAiAudioAtRef = useRef<number>(0);

  // --- Voice / noise gating for barge-in ---
  const noiseAnalyzerRef = useRef<NoiseAnalyzer>(new NoiseAnalyzer());
  const voiceBargeInRef = useRef<VoiceBargeInDetector>(
    new VoiceBargeInDetector(),
  );

  // --- Old behavior: while AI speaks, user mic should be off; auto-on when AI ends ---
  const userMutedManuallyRef = useRef(false);
  const autoMutedByAiRef = useRef(false);
  const pendingAutoResumeRef = useRef(false);
  const forceEndUserTurnRef = useRef<
    (reason?: "mute" | "manual" | "cleanup") => void
  >(() => {});
  const resumeSpeekingRef = useRef<() => Promise<void>>(async () => {});
  const isPushToTalkRef = useRef(isPushToTalk);
  const cancelPushToTalkEndRef = useRef<TrailingSilenceCancel | null>(null);
  const isPushToTalkEndingRef = useRef(false);
  const pushToTalkHeldRef = useRef(false);

  useEffect(() => {
    isPushToTalkRef.current = isPushToTalk;
  }, [isPushToTalk]);

  // Track AI playback using the actual `WavStreamPlayer` stream lifecycle.
  const [aiPlaybackActive, setAiPlaybackActive] = useState(false);
  const aiPlaybackActiveRef = useRef(false);
  const onAiPlaybackStateChangeRef = useRef(onAiPlaybackStateChange);
  const onUserBargeInRef = useRef(onUserBargeIn);
  const onUserSpeakingStateChangeRef = useRef(onUserSpeakingStateChange);
  const onNoiseWarningRef = useRef(onNoiseWarning);

  useEffect(() => {
    onAiPlaybackStateChangeRef.current = onAiPlaybackStateChange;
  }, [onAiPlaybackStateChange]);

  useEffect(() => {
    onUserBargeInRef.current = onUserBargeIn;
  }, [onUserBargeIn]);

  useEffect(() => {
    onUserSpeakingStateChangeRef.current = onUserSpeakingStateChange;
  }, [onUserSpeakingStateChange]);

  useEffect(() => {
    onNoiseWarningRef.current = onNoiseWarning;
  }, [onNoiseWarning]);

  useEffect(() => {
    onSessionErrorRef.current = onSessionError;
  }, [onSessionError]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const clearAiDialogueStartWatch = useCallback(() => {
    if (aiStartTimeoutRef.current) {
      clearTimeout(aiStartTimeoutRef.current);
      aiStartTimeoutRef.current = null;
    }
  }, []);

  const markAiDialogueStarted = useCallback(() => {
    hasAIDialogueStartedRef.current = true;
    clearAiDialogueStartWatch();
  }, [clearAiDialogueStartWatch]);

  const reportSessionError = useCallback((error: LiveSpeechSessionError) => {
    if (sessionErrorActiveRef.current) return;
    sessionErrorActiveRef.current = true;
    lastSessionErrorCodeRef.current = error.code;
    setIsMicReady(false);
    clearAiDialogueStartWatch();
    try {
      onSessionErrorRef.current?.(error);
    } catch (e) {
      console.warn("onSessionError callback failed:", e);
    }
  }, [clearAiDialogueStartWatch]);

  const armAiDialogueStartWatch = useCallback(() => {
    clearAiDialogueStartWatch();
    hasAIDialogueStartedRef.current = false;
    aiStartTimeoutRef.current = setTimeout(() => {
      if (!hasAIDialogueStartedRef.current) {
        const userNeverSpoke = !userHasSpokenRef.current;
        reportSessionError({
          code: "ai_dialogue_timeout",
          message: userNeverSpoke
            ? "Please start speaking to begin the conversation."
            : "The AI didn't start in time. Please try again or end the session.",
          retryable: true,
          canFallbackAudioOnly: false,
        });
      }
    }, AI_DIALOGUE_START_TIMEOUT_MS);
  }, [clearAiDialogueStartWatch, reportSessionError]);

  const waitForSocketConnected = useCallback(
    (socket: Socket | null, timeoutMs = 10_000) =>
      new Promise<boolean>((resolve) => {
        if (!socket) {
          resolve(false);
          return;
        }
        if (socket.connected) {
          resolve(true);
          return;
        }

        let settled = false;
        const finish = (ok: boolean) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          socket.off("connect", onConnect);
          resolve(ok);
        };

        const onConnect = () => finish(true);
        const timer = window.setTimeout(() => finish(false), timeoutMs);
        socket.on("connect", onConnect);
      }),
    [],
  );

  // Single silent reconnect attempt
  const attemptSilentReconnect = useCallback(
    async (timeoutMs = SILENT_RECONNECT_TIMEOUT_MS): Promise<boolean> => {
      if (isBrowserOffline()) return false;

      const socket = socketRef.current;
      if (!socket) return false;
      if (socket.connected) return true;

      if (silentReconnectInFlightRef.current) {
        return waitForSocketConnected(socket, timeoutMs);
      }

      silentReconnectInFlightRef.current = true;
      try {
        try {
          socket.connect();
        } catch {
          // ignore
        }
        return await waitForSocketConnected(socket, timeoutMs);
      } finally {
        silentReconnectInFlightRef.current = false;
      }
    },
    [waitForSocketConnected],
  );

  const reportConnectionLostAfterFailedReconnect = useCallback(() => {
    if (intentionalDisconnectRef.current) return;
    if (isBrowserOffline()) {
      reportSessionError({
        ...OFFLINE_SESSION_ERROR,
        canFallbackAudioOnly: false,
      });
      return;
    }
    reportSessionError(CONNECTION_LOST_SESSION_ERROR);
  }, [reportSessionError]);

  // Wait for an in-progress connect (or create+connect) before treating mic as lost.
  const ensureSocketConnectedForResume = useCallback(
    async (timeoutMs = 10_000): Promise<boolean> => {
      if (isBrowserOffline()) return false;

      const deadline = Date.now() + timeoutMs;
      while (!socketRef.current && Date.now() < deadline) {
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 50);
        });
      }

      const socket = socketRef.current;
      if (!socket) return false;
      if (socket.connected) return true;

      const remaining = Math.max(500, deadline - Date.now());
      if (await waitForSocketConnected(socket, remaining)) return true;
      return attemptSilentReconnect(Math.max(500, deadline - Date.now()));
    },
    [attemptSilentReconnect, waitForSocketConnected],
  );

  const emitStartAiDialogue = useCallback(() => {
    if (!socketRef.current) return;
    socketRef.current.emit("start_ai_dialogue", {
      user_id,
      ...additionalMessagePropsRef.current,
    });
    if (!hasAIDialogueStartedRef.current) {
      armAiDialogueStartWatch();
    }
  }, [user_id, armAiDialogueStartWatch]);

  useEffect(() => {
    setIsMutedRef.current = setIsMuted;
  }, [setIsMuted]);

  const updateAiPlaybackActive = useCallback((next: boolean) => {
    if (next === aiPlaybackActiveRef.current) return;
    aiPlaybackActiveRef.current = next;
    setAiPlaybackActive(next);
    try {
      onAiPlaybackStateChangeRef.current?.(next);
    } catch (e) {
      console.warn("onAiPlaybackStateChange callback failed:", e);
    }

    // Old algorithm: hard mute the user mic while AI is speaking; restore automatically afterwards.
    // We do NOT auto-unmute if the user explicitly muted manually.
    if (next) {
      pendingAutoResumeRef.current = false;

      if (!isMutedRef.current) {
        cancelPushToTalkEndSequence();
        isPushToTalkEndingRef.current = false;
        autoMutedByAiRef.current = true;
        isMutedRef.current = true;
        try {
          forceEndUserTurnRef.current("mute");
        } catch {}
        setIsMutedRef.current(true);
        isCurrentlySendingRef.current = false;
        try {
          onUserSpeakingStateChangeRef.current?.(false);
        } catch (e) {
          console.warn("onUserSpeakingStateChange callback failed:", e);
        }
        chunkQueue.current = [];
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
      }
    } else {
      if (isPushToTalkRef.current) {
        autoMutedByAiRef.current = false;
        pendingAutoResumeRef.current = false;
        return;
      }

      if (userMutedManuallyRef.current) {
        autoMutedByAiRef.current = false;
        pendingAutoResumeRef.current = false;
        return;
      }

      const shouldAutoResume =
        autoMutedByAiRef.current || pendingAutoResumeRef.current;
      autoMutedByAiRef.current = false;
      pendingAutoResumeRef.current = false;

      if (shouldAutoResume && isMutedRef.current) {
        void (async () => {
          try {
            await resumeSpeekingRef.current();
          } catch (e) {
            console.warn("Auto resumeSpeeking failed:", e);
          }
        })();
      }
    }
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      // `WavStreamPlayer` sets `.stream = null` when the worklet stops (queue drained / interrupted).
      const playerAny = playerRef.current as any;
      const streamPlaying = Boolean(playerAny && playerAny.stream);
      const recentlyReceivedAiPcm =
        Date.now() - (lastAiAudioAtRef.current || 0) < 2500;
      const isPlaying =
        streamPlaying || recentlyReceivedAiPcm || aiIsSpeakingRef.current;
      updateAiPlaybackActive(isPlaying);
    }, 60);
    return () => window.clearInterval(interval);
  }, [updateAiPlaybackActive]);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    isTabVisibleRef.current = isTabVisible;
  }, [isTabVisible]);

  const ensureRecorderActive = async () => {
    if (stopRequestedRef.current) {
      return;
    }

    if (!recorderRef.current) {
      const wavRecorder = new WavRecorder({ sampleRate: 24000 });
      recorderRef.current = wavRecorder;
      await wavRecorder.begin();
      if (stopRequestedRef.current) {
        stopAllTracks(wavRecorder);
        recorderRef.current = null;
        return;
      }
      await wavRecorder.record(userMicDataHandler);
      if (stopRequestedRef.current) {
        stopAllTracks(wavRecorder);
        recorderRef.current = null;
        return;
      }
      syncMicMediaStream();
      return;
    }

    try {
      const status = recorderRef.current.getStatus?.();
      if (status !== "recording") {
        // @ts-ignore
        await recorderRef.current.audioContext?.resume?.();

        if (status === "paused") {
          await recorderRef.current.record(userMicDataHandler);
        } else {
          // If status is "ended", we need to recreate
          const oldRecorder = recorderRef.current;
          stopAllTracks(oldRecorder);
          recorderRef.current = null;

          if (stopRequestedRef.current) {
            return;
          }

          const wavRecorder = new WavRecorder({ sampleRate: 24000 });
          recorderRef.current = wavRecorder;
          await wavRecorder.begin();
          if (stopRequestedRef.current) {
            stopAllTracks(wavRecorder);
            recorderRef.current = null;
            return;
          }
          await wavRecorder.record(userMicDataHandler);
          if (stopRequestedRef.current) {
            stopAllTracks(wavRecorder);
            recorderRef.current = null;
            return;
          }
        }
      }
      syncMicMediaStream();
    } catch (e) {
      const oldRecorder = recorderRef.current;
      if (oldRecorder) {
        stopAllTracks(oldRecorder);
      }
      recorderRef.current = null;

      if (stopRequestedRef.current) {
        return;
      }

      try {
        const wavRecorder = new WavRecorder({ sampleRate: 24000 });
        recorderRef.current = wavRecorder;
        await wavRecorder.begin();
        if (stopRequestedRef.current) {
          stopAllTracks(wavRecorder);
          recorderRef.current = null;
          return;
        }
        await wavRecorder.record(userMicDataHandler);
        if (stopRequestedRef.current) {
          stopAllTracks(wavRecorder);
          recorderRef.current = null;
          return;
        }
        syncMicMediaStream();
      } catch (err) {
        console.error("[ensureRecorderActive] failed reinit:", err);
      }
    }
  };

  useEffect(() => {
    shouldStopAIRef.current = shouldStopAI;

    if (shouldStopAI && aiIsSpeakingRef.current) {
      if (playerRef.current) {
        playerRef.current.interrupt();
      }
      aiIsSpeakingRef.current = false;
      updateAiPlaybackActive(false);
      if (aiSilenceTimeoutRef.current) {
        clearTimeout(aiSilenceTimeoutRef.current);
        aiSilenceTimeoutRef.current = null;
      }
    }
  }, [shouldStopAI, updateAiPlaybackActive]);

  const sendChunkForce = useCallback(
    (chunk: Int16Array) => {
      const message = {
        user_id,
        chunk,
        ...additionalMessagePropsRef.current,
      };

      if (socketRef.current?.connected) {
        socketRef.current.emit("audio_chunk", message);
      } else {
        console.error("Socket not available for audio_chunk");
      }
    },
    [user_id],
  );

  const onNewChunk = useCallback(
    (chunk: Int16Array) => {
      // Push-to-talk: stream immediately while the button is held.
      if (
        isPushToTalkRef.current &&
        pushToTalkHeldRef.current &&
        !isMutedRef.current &&
        isCurrentlySendingRef.current
      ) {
        sendChunkForce(chunk);
        return;
      }

      if (chunkQueue.current.length < MAX_QUEUE_SIZE) {
        chunkQueue.current.push(chunk);
      } else {
        const oldestChunk = chunkQueue.current.shift();
        if (
          oldestChunk &&
          isCurrentlySendingRef.current &&
          !isMutedRef.current
        ) {
          sendChunkForce(oldestChunk);
        }
        chunkQueue.current.push(chunk);
      }
    },
    [sendChunkForce],
  );

  const flushQueue = useCallback(() => {
    while (chunkQueue.current.length > 0) {
      const oldestChunk = chunkQueue.current.shift();

      if (oldestChunk) {
        sendChunkForce(oldestChunk);
      }
    }
  }, [sendChunkForce]);

  const forceEndUserTurn = useCallback(
    (reason: "mute" | "manual" | "cleanup" = "manual") => {
      if (isProcessingMessageEndRef.current) return;

      const hasBufferedAudio = chunkQueue.current.length > 0;
      const isInUserTurn =
        userHasSpokenRef.current ||
        isCurrentlySendingRef.current ||
        hasBufferedAudio;

      if (!isInUserTurn) return;

      isProcessingMessageEndRef.current = true;

      try {
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }

        flushQueue();
      } catch {
        // ignore
      }

      shouldPauseAIPlaybackRef.current = false;

      chunkQueue.current = [];
      isCurrentlySendingRef.current = false;
      try {
        onUserSpeakingStateChangeRef.current?.(false);
      } catch (e) {
        console.warn("onUserSpeakingStateChange callback failed:", e);
      }
      userHasSpokenRef.current = false;
      voiceBargeInRef.current.reset();

      if (socketRef.current?.connected) {
        try {
          socketRef.current.emit("user_message_end", {
            user_id,
            ...additionalMessagePropsRef.current,
            reason,
          });
        } catch {
          console.error("Socket is not available to send user_message_end");
        }
      }

      try {
        onTurnCompleted?.();
      } catch {}

      setTimeout(() => {
        isProcessingMessageEndRef.current = false;
      }, 600);
    },
    [flushQueue, onTurnCompleted, user_id],
  );

  // Keep a ref to avoid dependency churn in updateAiPlaybackActive
  forceEndUserTurnRef.current = forceEndUserTurn;

  const cancelPushToTalkEndSequence = useCallback(() => {
    if (cancelPushToTalkEndRef.current) {
      cancelPushToTalkEndRef.current();
      cancelPushToTalkEndRef.current = null;
    }
  }, []);

  const endPushToTalkTurn = useCallback(() => {
    const wasHeld = pushToTalkHeldRef.current;
    pushToTalkHeldRef.current = false;

    userMutedManuallyRef.current = true;
    pendingAutoResumeRef.current = false;
    autoMutedByAiRef.current = false;
    isMutedRef.current = true;
    setIsMutedRef.current(true);

    const hasBufferedAudio = chunkQueue.current.length > 0;
    const isInUserTurn =
      wasHeld ||
      userHasSpokenRef.current ||
      isCurrentlySendingRef.current ||
      hasBufferedAudio;

    // Always commit a held PTT press — otherwise a quiet mic / VAD miss
    // drops the turn and the AI never answers.
    if (!isInUserTurn) {
      return;
    }

    if (isPushToTalkEndingRef.current) {
      return;
    }

    cancelPushToTalkEndSequence();
    isPushToTalkEndingRef.current = true;
    userHasSpokenRef.current = true;

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    flushQueue();

    if (!isCurrentlySendingRef.current) {
      isCurrentlySendingRef.current = true;
    }

    const sendSilenceChunk = (chunk: Int16Array) => {
      onNewChunk(chunk);
      sendChunkForce(chunk);
    };

    cancelPushToTalkEndRef.current = scheduleTrailingSilenceChunks(
      sendSilenceChunk,
      () => {
        cancelPushToTalkEndRef.current = null;
        isPushToTalkEndingRef.current = false;
        isCurrentlySendingRef.current = false;
        try {
          onUserSpeakingStateChangeRef.current?.(false);
        } catch (e) {
          console.warn("onUserSpeakingStateChange callback failed:", e);
        }
        forceEndUserTurn("mute");
      },
      END_OF_UTTERANCE_SILENCE_MS,
    );
  }, [cancelPushToTalkEndSequence, flushQueue, forceEndUserTurn, onNewChunk, sendChunkForce]);

  const sendAudio = (buffer: Int16Array) => {
    const chunk = new Int16Array(buffer);

    for (let i = 0; i < chunk.length; i += SAMPLES_PER_SUBCHUNK) {
      const subChunk = chunk.slice(i, i + SAMPLES_PER_SUBCHUNK);

      // Keep rolling noise windows while muted (browser mic stays on in roleplay).
      // Suppress warn evaluation until the user mic is active so we don't consume
      // the one-shot warning flag before the first push-to-talk press.
      const suppressNoiseWarning = isPushToTalkRef.current
        ? !pushToTalkHeldRef.current || isMutedRef.current
        : isMutedRef.current;

      const noiseResult = noiseAnalyzerRef.current.processChunk(
        subChunk,
        suppressNoiseWarning,
      );

      if (noiseResult?.shouldWarn) {
        try {
          if (onNoiseWarningRef.current) {
            onNoiseWarningRef.current({
              shouldWarn: noiseResult.shouldWarn,
              message: noiseResult.message,
            });
          }
        } catch (e) {
          console.error("onNoiseWarning callback failed:", e);
        }
      }

      if (isMutedRef.current) {
        if (!isSilentSubChunkFixed(subChunk, -20)) {
          triggerMicrophoneTip();
        }
        continue;
      } else {
        // --- VAD-lite threshold (adapts to noise floor) ---
        const vadSilenceDbfs = noiseAnalyzerRef.current.getVadSilenceDbfs();
        const { dbfs } = rmsAndDbfsInt16(subChunk);
        const isSilent = dbfs < vadSilenceDbfs;

        // --- Voice-like gating (reduce false barge-in from non-speech noises) ---
        const { voiceActive } = voiceBargeInRef.current.process(
          subChunk,
          vadSilenceDbfs,
        );

        if (isSilent) {
          // During push-to-talk hold, silence must NOT end the turn — only
          // releasing the mic button commits via endPushToTalkTurn().
          if (pushToTalkHeldRef.current) {
            onNewChunk(subChunk);
            continue;
          }

          if (isCurrentlySendingRef.current && !silenceTimeoutRef.current) {
            silenceTimeoutRef.current = setTimeout(() => {
              flushQueue();

              isCurrentlySendingRef.current = false;
              try {
                onUserSpeakingStateChangeRef.current?.(false);
              } catch (e) {
                console.warn("onUserSpeakingStateChange callback failed:", e);
              }
              silenceTimeoutRef.current = null;

              if (playerRef.current) {
                try {
                  if (typeof playerRef.current.interrupt === "function") {
                    try {
                      playerRef.current.interrupt();
                    } catch (e) {
                      // ignore
                    }
                  }

                  const wavStreamPlayer = new WavStreamPlayer({
                    sampleRate: 24000,
                  });
                  playerRef.current = wavStreamPlayer;

                  wavStreamPlayer
                    .connect()
                    .then(() => {
                      shouldPauseAIPlaybackRef.current = false;
                    })
                    .catch(() => {
                      shouldPauseAIPlaybackRef.current = false;
                    });
                } catch (error) {
                  shouldPauseAIPlaybackRef.current = false;
                }
              } else {
                shouldPauseAIPlaybackRef.current = false;
              }

              voiceBargeInRef.current.reset();

              if (
                userHasSpokenRef.current &&
                !isProcessingMessageEndRef.current
              ) {
                isProcessingMessageEndRef.current = true;

                setTimeout(() => {
                  flushQueue();
                  userHasSpokenRef.current = false;

                  if (socketRef.current?.connected) {
                    socketRef.current.emit("user_message_end", {
                      user_id,
                      ...additionalMessagePropsRef.current,
                    });
                  } else {
                    console.error(
                      "Socket is not available to send user_message_end",
                    );
                  }

                  if (onTurnCompleted) {
                    onTurnCompleted();
                  }

                  setTimeout(() => {
                    isProcessingMessageEndRef.current = false;
                  }, 2000);
                }, 100);
              }
            }, END_OF_UTTERANCE_SILENCE_MS);
          }
        } else {
          if (!isCurrentlySendingRef.current) {
            const now = Date.now();
            const inCooldown = now < bargeInCooldownUntilRef.current;
            const aiIsStillPlaying =
              aiIsSpeakingRef.current || aiPlaybackActiveRef.current;
            const shouldTriggerBargeIn =
              voiceActive && !(aiIsStillPlaying && inCooldown);
            if (shouldTriggerBargeIn) {
              flushQueue();
              isCurrentlySendingRef.current = true;
              userHasSpokenRef.current = true;
              shouldPauseAIPlaybackRef.current = true;
              if (
                playerRef.current &&
                typeof playerRef.current.interrupt === "function"
              ) {
                try {
                  playerRef.current.interrupt();
                } catch (error) {
                  console.warn("Error interrupting AI playback:", error);
                }
              }
              try {
                onUserBargeInRef.current?.();
              } catch (e) {
                console.warn("onUserBargeIn callback failed:", e);
              }
              try {
                onUserSpeakingStateChangeRef.current?.(true);
              } catch (e) {
                console.warn("onUserSpeakingStateChange callback failed:", e);
              }
            }
          } else if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
        }
      }

      onNewChunk(subChunk);
    }
  };

  const userMicDataHandler = (data: { mono: Int16Array; raw: Int16Array }) => {
    lastUserAudioAtRef.current = Date.now();
    try {
      onUserPcmChunk?.(data.mono);
    } catch (e) {
      console.warn("onUserPcmChunk callback failed:", e);
    }
    sendAudio(data.mono);
  };

  const syncMicMediaStream = useCallback(() => {
    const stream = (recorderRef.current as { stream?: MediaStream | null } | null)
      ?.stream;
    setMicMediaStream((prev) => {
      const next = stream ?? null;
      return prev === next ? prev : next;
    });
  }, []);

  // Helper to force-stop all MediaStream tracks (critical for Edge/Safari)
  const stopAllTracks = (recorder: WavRecorder | null) => {
    try {
      if (recorder && (recorder as any).stream) {
        const stream = (recorder as any).stream as MediaStream;
        if (stream && stream.getTracks) {
          stream.getTracks().forEach((track) => {
            try {
              if (track.readyState !== "ended") {
                track.stop();
              }
            } catch (e) {
              console.warn("Error stopping track:", e);
            }
          });
        }
      }
    } catch (e) {
      console.warn("Error accessing recorder stream:", e);
    }
    setMicMediaStream(null);
  };

  // Helper to close AudioContext in player (Edge needs explicit cleanup)
  const closePlayerContext = (player: WavStreamPlayer | null) => {
    try {
      if (player && (player as any).context) {
        const ctx = (player as any).context as AudioContext;
        if (ctx && ctx.state !== "closed") {
          ctx.close().catch(() => {});
        }
      }
    } catch (e) {
      console.warn("Error closing player context:", e);
    }
  };

  const initSpeeking = async () => {
    try {
      // Reset stop flag and increment init sequence
      stopRequestedRef.current = false;
      const currentInitSeq = ++initSeqRef.current;

      initializeSocket();

      // Check if stop was requested during socket init
      if (stopRequestedRef.current || currentInitSeq !== initSeqRef.current) {
        return;
      }

      const wavRecorder = new WavRecorder({ sampleRate: 24000 });
      recorderRef.current = wavRecorder;

      const wavStreamPlayer = new WavStreamPlayer({ sampleRate: 24000 });
      playerRef.current = wavStreamPlayer;

      chunkQueue.current = [];
      noiseAnalyzerRef.current.reset();
      voiceBargeInRef.current.reset();

      await wavRecorder.begin();

      // Check again after begin() - if stop was requested, cleanup immediately
      if (stopRequestedRef.current || currentInitSeq !== initSeqRef.current) {
        stopAllTracks(wavRecorder);
        recorderRef.current = null;
        closePlayerContext(wavStreamPlayer);
        playerRef.current = null;
        return;
      }

      await wavStreamPlayer.connect();

      // Check again after connect()
      if (stopRequestedRef.current || currentInitSeq !== initSeqRef.current) {
        stopAllTracks(wavRecorder);
        recorderRef.current = null;
        closePlayerContext(wavStreamPlayer);
        playerRef.current = null;
        return;
      }

      try {
        // @ts-ignore
        await wavStreamPlayer.context?.resume?.();
        // @ts-ignore
        await wavRecorder.audioContext?.resume?.();
      } catch {
        // ignore autoplay/resume failures; PTT may resume later
      }

      await wavRecorder.record(userMicDataHandler);

      // Final check after record()
      if (stopRequestedRef.current || currentInitSeq !== initSeqRef.current) {
        stopAllTracks(wavRecorder);
        recorderRef.current = null;
        closePlayerContext(wavStreamPlayer);
        playerRef.current = null;
        return;
      }

      syncMicMediaStream();

      if (socketRef.current && isLessonDialogue) {
        setTimeout(() => {
          emitStartAiDialogue();
        }, 500);
      } else if (socketRef.current && !isLessonDialogue) {
      }
    } catch (error) {
      console.error("Error initializing audio:", error);

      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isDenied =
        errorMessage.includes("denied") || errorMessage.includes("Permission");

      if (onMicrophoneError) {
        onMicrophoneError();
      }

      if (isDenied) {
        alert(
          "Microphone access is denied. Please enable it in your browser settings and try again by clicking the microphone button.",
        );
      } else {
        alert(
          "Failed to initialize microphone. Please check your device and try again.",
        );
      }
    }
  };

  const stopSpeeking = async () => {
    try {
      stopRequestedRef.current = true;
      intentionalDisconnectRef.current = true;
      clearAiDialogueStartWatch();
      pushToTalkHeldRef.current = false;

      const wasSpeeking =
        recorderRef.current &&
        recorderRef.current.getStatus() === "recording" &&
        !isMuted;

      // CRITICAL: Stop tracks IMMEDIATELY before calling end() (Edge/Safari fix)
      if (recorderRef.current) {
        stopAllTracks(recorderRef.current);
      }

      if (recorderRef.current && recorderRef.current.getStatus() !== "ended") {
        try {
          const endPromise = recorderRef.current.end();
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("end() timeout")), 2000),
          );
          await Promise.race([endPromise, timeoutPromise]);
        } catch (e) {
          console.warn(
            "recorder.end() failed or timed out, forcing cleanup:",
            e,
          );
          stopAllTracks(recorderRef.current);
        }
        recorderRef.current = null;
      }

      if (recorderRef.current) {
        stopAllTracks(recorderRef.current);
      }

      if (wasSpeeking && isCurrentlySendingRef.current) {
        flushQueue();
      }
      chunkQueue.current = [];

      // Close player and its AudioContext
      if (playerRef.current) {
        playerRef.current.interrupt();
        closePlayerContext(playerRef.current);
        playerRef.current = null;
      }
      updateAiPlaybackActive(false);

      isCurrentlySendingRef.current = false;
      try {
        onUserSpeakingStateChangeRef.current?.(false);
      } catch (e) {
        console.warn("onUserSpeakingStateChange callback failed:", e);
      }
      voiceBargeInRef.current.reset();

      if (aiSilenceTimeoutRef.current) {
        clearTimeout(aiSilenceTimeoutRef.current);
        aiSilenceTimeoutRef.current = null;
      }

      aiIsSpeakingRef.current = false;
      updateAiPlaybackActive(false);

      stopAllVisualizerMicStreams();

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    } catch (error) {
      console.error("Failed to end realtime speech:", error);
      if (recorderRef.current) {
        stopAllTracks(recorderRef.current);
      }
      stopAllVisualizerMicStreams();
    }
  };

  // Hard-stop AI playback and drop already-queued PCM so barge-in feels instant.
  const interruptAiPlaybackForBargeIn = async () => {
    shouldPauseAIPlaybackRef.current = true;
    lastAiAudioAtRef.current = 0;
    aiIsSpeakingRef.current = false;
    if (aiSilenceTimeoutRef.current) {
      clearTimeout(aiSilenceTimeoutRef.current);
      aiSilenceTimeoutRef.current = null;
    }
    updateAiPlaybackActive(false);

    try {
      if (
        playerRef.current &&
        typeof playerRef.current.interrupt === "function"
      ) {
        void playerRef.current.interrupt();
      }
    } catch (error) {
      console.warn("Error interrupting AI playback on barge-in:", error);
    }

    try {
      closePlayerContext(playerRef.current);
      const wavStreamPlayer = new WavStreamPlayer({ sampleRate: 24000 });
      playerRef.current = wavStreamPlayer;
      await wavStreamPlayer.connect();
      try {
        // @ts-ignore
        await wavStreamPlayer.context?.resume?.();
      } catch {
        // ignore autoplay/resume failures
      }
    } catch (error) {
      console.warn("Failed to reset audio player after barge-in:", error);
    }

    try {
      onUserBargeInRef.current?.();
    } catch (e) {
      console.warn("onUserBargeIn callback failed:", e);
    }

    emitBestEffortInterrupt(socketRef.current, {
      user_id,
      ...additionalMessagePropsRef.current,
    });
  };

  // If AI is currently speaking, by default keep mic muted and schedule auto-resume.
  // But allow an explicit user unmute while AI is speaking (barge-in use case).
  const resumeSpeeking = async (opts?: {
    allowDuringAi?: boolean;
    skipBargeInCooldown?: boolean;
  }) => {
    cancelPushToTalkEndSequence();
    isPushToTalkEndingRef.current = false;
    const aiIsPlayingNow =
      aiPlaybackActiveRef.current || aiIsSpeakingRef.current;
    if (aiIsPlayingNow && !opts?.allowDuringAi) {
      pendingAutoResumeRef.current = true;
      autoMutedByAiRef.current = true;
      userMutedManuallyRef.current = false;
      isMutedRef.current = true;
      setIsMuted(true);
      return;
    }

    // Push-to-talk / explicit unmute during AI: stop playback immediately.
    const shouldBargeInInterrupt =
      Boolean(opts?.allowDuringAi) && aiIsPlayingNow;
    if (shouldBargeInInterrupt) {
      await interruptAiPlaybackForBargeIn();
    }

    userMutedManuallyRef.current = false;
    pendingAutoResumeRef.current = false;
    autoMutedByAiRef.current = false;

    await ensureRecorderActive();

    // If the recorder still has no live tracks after video teardown, rebuild it.
    try {
      const stream = (recorderRef.current as any)?.stream as
        | MediaStream
        | undefined;
      const liveTracks =
        stream
          ?.getAudioTracks?.()
          .filter((t) => t.readyState === "live")
          .length ?? 0;
      if (liveTracks < 1) {
        if (recorderRef.current) {
          stopAllTracks(recorderRef.current);
          recorderRef.current = null;
        }
        await ensureRecorderActive();
      }
    } catch {
      // ignore — ensureRecorderActive already best-effort
    }

    const shouldSkipCooldown =
      Boolean(opts?.skipBargeInCooldown) ||
      (aiIsPlayingNow && Boolean(opts?.allowDuringAi));
    bargeInCooldownUntilRef.current = shouldSkipCooldown
      ? 0
      : Date.now() + 3000;

    setIsMuted(false);
    isMutedRef.current = false;

    // Push-to-talk must start streaming immediately. Waiting for voiceActive
    // VAD often drops the whole turn after video→audio fallback.
    if (isPushToTalkRef.current) {
      pushToTalkHeldRef.current = true;
      isCurrentlySendingRef.current = true;
      userHasSpokenRef.current = true;
      try {
        onUserSpeakingStateChangeRef.current?.(true);
      } catch (e) {
        console.warn("onUserSpeakingStateChange callback failed:", e);
      }
    } else {
      isCurrentlySendingRef.current = false;
      try {
        onUserSpeakingStateChangeRef.current?.(false);
      } catch (e) {
        console.warn("onUserSpeakingStateChange callback failed:", e);
      }
    }

    chunkQueue.current = [];
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    const playerAny = playerRef.current as any;
    const isPlayerConnected = Boolean(playerAny && playerAny.stream);
    if (
      playerRef.current &&
      typeof playerRef.current.connect === "function" &&
      !isPlayerConnected
    ) {
      try {
        await playerRef.current.connect();
      } catch (error) {
        console.warn("Failed to restart player:", error);
      }
    }

    try {
      // @ts-ignore
      await playerRef.current?.context?.resume?.();
      // @ts-ignore
      await recorderRef.current?.audioContext?.resume?.();
    } catch {
      // ignore
    }

    isProcessingMessageEndRef.current = false;
    if (!isPushToTalkRef.current) {
      userHasSpokenRef.current = false;
    }
    shouldStopAIRef.current = false;
    if (!shouldBargeInInterrupt) {
      shouldPauseAIPlaybackRef.current = false;
    }
    voiceBargeInRef.current.reset();
    noiseAnalyzerRef.current.reset();

    if (!socketRef.current?.connected) {
      const recovered = await ensureSocketConnectedForResume();
      if (!recovered || !socketRef.current?.connected) {
        reportConnectionLostAfterFailedReconnect();
        pushToTalkHeldRef.current = false;
        isMutedRef.current = true;
        setIsMuted(true);
        try {
          onUserSpeakingStateChangeRef.current?.(false);
        } catch {
          // ignore
        }
        return;
      }
    }

    const aiIsPlaying = aiPlaybackActiveRef.current || aiIsSpeakingRef.current;
    if (socketRef.current && !aiIsPlaying && !shouldBargeInInterrupt) {
      socketRef.current.emit("resume_dialogue", {
        user_id,
        ...additionalMessagePropsRef.current,
      });

      socketRef.current.emit("user_ready", {
        user_id,
        ...additionalMessagePropsRef.current,
      });

      setTimeout(() => {
        socketRef.current?.emit("user_ready_to_respond", {
          user_id,
          ...additionalMessagePropsRef.current,
        });
      }, 300);
    }

    // Do NOT re-emit start_ai_dialogue on Push-to-Talk. That resets the OpenAI
    // session mid-utterance (especially after Retry when hasAIDialogueStarted
    // is still false) and the AI never answers.
    if (!isPushToTalkRef.current) {
      setTimeout(() => {
        if (!hasAIDialogueStartedRef.current && socketRef.current) {
          emitStartAiDialogue();
        }
      }, 600);
    }
  };

  // Keep a ref to avoid dependency churn in updateAiPlaybackActive
  resumeSpeekingRef.current = resumeSpeeking;

  const pauseSpeeking = async () => {
    if (isPushToTalkRef.current) {
      endPushToTalkTurn();
      return;
    }

    if (
      recorderRef.current &&
      recorderRef.current.getStatus() !== "recording"
    ) {
      console.error(
        "Cannot pause recording, the recording has not started yet.",
      );
      stopSpeeking();
      onClose();
      return;
    }

    // Manual mute by user should be respected (no auto-unmute after AI).
    userMutedManuallyRef.current = true;
    pendingAutoResumeRef.current = false;
    autoMutedByAiRef.current = false;

    isMutedRef.current = true;

    forceEndUserTurn("mute");

    setIsMuted(true);
    isCurrentlySendingRef.current = false;
    try {
      onUserSpeakingStateChangeRef.current?.(false);
    } catch (e) {
      console.warn("onUserSpeakingStateChange callback failed:", e);
    }

    chunkQueue.current = [];
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  };

  const initializeSocket = () => {
    if (socketRef.current && socketRef.current.connected) {
      return;
    }

    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

    if (socketRef.current) {
      intentionalDisconnectRef.current = true;
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const token = getAuthToken() ?? auth?.token;
    if (!token) {
      console.error("Token is required for socket connection");
      throw new Error("Token is required for socket connection");
    }

    // Ensure auth object is properly structured with token
    const authWithToken = {
      ...(auth || {}),
      token,
    };
    // Keep shared cookie aligned with auth.token for WS handshake checks.
    syncAuthCookie(token);

    intentionalDisconnectRef.current = false;
    socketRef.current = io(SOCKET_URL, { auth: authWithToken });

    // Ensure auth is set on the socket object for reconnections
    socketRef.current.auth = authWithToken;

    socketRef.current.on("connect", () => {
      const currentToken = getAuthToken() ?? auth?.token;
      if (currentToken && socketRef.current) {
        syncAuthCookie(currentToken);
        socketRef.current.auth = {
          ...(auth || {}),
          token: currentToken,
        };
      }

      if (onSocketConnectedRef.current) {
        onSocketConnectedRef.current();
      }
    });

    socketRef.current.on("disconnect", (reason) => {
      if (intentionalDisconnectRef.current) return;
      console.warn("Realtime socket disconnected:", reason);

      // Offline → show immediately. Otherwise try one silent reconnect so brief
      // blips (ping timeout / transport close) don't pop the modal.
      if (isBrowserOffline()) {
        reportSessionError({
          ...OFFLINE_SESSION_ERROR,
          canFallbackAudioOnly: false,
        });
        return;
      }

      void (async () => {
        const recovered = await attemptSilentReconnect();
        if (intentionalDisconnectRef.current) return;
        if (recovered && socketRef.current?.connected) {
          console.warn("Realtime socket recovered after silent reconnect");
          hasAIDialogueStartedRef.current = false;
          emitStartAiDialogue();
          return;
        }
        reportConnectionLostAfterFailedReconnect();
      })();
    });

    socketRef.current.on("connect_error", (err: any) => {
      if (intentionalDisconnectRef.current) return;
      if (silentReconnectInFlightRef.current) return;
      if (initialConnectInFlightRef.current) return;
      reportSessionError({
        ...OFFLINE_SESSION_ERROR,
        canFallbackAudioOnly: false,
        message: isBrowserOffline()
          ? OFFLINE_SESSION_ERROR.message
          : "No internet connection. Check your network and try again.",
      });
      console.error("Realtime socket connect_error:", err);
    });

    socketRef.current.on("ai_dialogue_started", () => {
      markAiDialogueStarted();
    });

    // Keep the existing `seconds_allowed` timer in sync for screens that rely on it.
    socketRef.current.on("response", (msg: any) => {
      try {
        const secondsAllowed =
          typeof msg?.seconds_allowed === "number"
            ? msg.seconds_allowed
            : typeof msg?.data?.seconds_allowed === "number"
              ? msg.data.seconds_allowed
              : null;
        if (typeof secondsAllowed === "number" && isFinite(secondsAllowed)) {
          const { setLessonSecondsAllowed, setLessonSecondsRemaining } =
            useAppStore.getState();
          setLessonSecondsAllowed(secondsAllowed);
          setLessonSecondsRemaining(secondsAllowed);
          // Timer only means the session budget arrived — NOT that the AI spoke.
          // Clearing the start-watch here hid the timeout modal while AI stayed silent.
        }

        const rawError =
          typeof msg?.error === "string"
            ? msg.error
            : typeof msg?.data?.error === "string"
              ? msg.data.error
              : null;
        const mapped = mapResponseError(rawError);
        if (mapped) {
          if (mapped.code === "week_limit_exceeded") {
            intentionalDisconnectRef.current = true;
            shouldStopAIRef.current = true;
            if (playerRef.current) {
              try {
                playerRef.current.interrupt();
              } catch {
                // ignore
              }
            }
            if (socketRef.current) {
              socketRef.current.disconnect();
              socketRef.current = null;
            }
          }
          reportSessionError(mapped);
        }
      } catch (e) {
        console.warn("Failed to parse response event:", e);
      }
    });

    socketRef.current.on("realtime.error", (payload: any) => {
      const message =
        (typeof payload?.message === "string" && payload.message) ||
        (typeof payload?.error?.message === "string" &&
          payload.error.message) ||
        (typeof payload?.error === "string" && payload.error) ||
        "A realtime error occurred. You can retry or end the session.";
      reportSessionError({
        code: "realtime_error",
        message,
        retryable: true,
        canFallbackAudioOnly: false,
      });
    });

    socketRef.current.on("realtime.event", async (base64) => {
      if (!shouldStopAIRef.current) {
        markAiDialogueStarted();
      }

      const shouldPlay =
        playerRef.current &&
        !shouldStopAIRef.current &&
        !shouldPauseAIPlaybackRef.current;

      if (shouldPlay) {
        try {
          aiIsSpeakingRef.current = true;

          if (aiSilenceTimeoutRef.current) {
            clearTimeout(aiSilenceTimeoutRef.current);
            aiSilenceTimeoutRef.current = null;
          }

          const binaryStr = atob(base64);
          const len = binaryStr.length;
          const bytes = Uint8Array.from(binaryStr, (c) => c.charCodeAt(0));

          const remainder = bytes.length % 2;
          const buffer = bytes.buffer.slice(0, bytes.length - remainder);
          const chunk = new Int16Array(buffer);
          lastAiAudioAtRef.current = Date.now();

          try {
            onAiPcmChunk?.(chunk);
          } catch (e) {
            console.warn("onAiPcmChunk callback failed:", e);
          }

          if (
            playerRef.current &&
            typeof playerRef.current.add16BitPCM === "function"
          ) {
            playerRef.current.add16BitPCM(chunk);
            updateAiPlaybackActive(true);
          } else {
            console.warn("PlayerRef is not ready for audio playback", {
              playerRef: !!playerRef.current,
              hasAdd16BitPCM:
                playerRef.current &&
                typeof playerRef.current.add16BitPCM === "function",
            });
          }

          aiSilenceTimeoutRef.current = setTimeout(() => {
            aiIsSpeakingRef.current = false;
          }, 2000);
        } catch (error) {
          console.error("Error playing audio:", error);
          aiIsSpeakingRef.current = false;
          updateAiPlaybackActive(false);
        }
      }
    });
  };

  const stopAISpeaking = () => {
    shouldStopAIRef.current = true;
    lastAiAudioAtRef.current = 0;
    if (
      playerRef.current &&
      typeof playerRef.current.interrupt === "function"
    ) {
      try {
        playerRef.current.interrupt();
      } catch (error) {
        console.error("Error stopping audio playback:", error);
      }
    }
    aiIsSpeakingRef.current = false;
    updateAiPlaybackActive(false);
    if (aiSilenceTimeoutRef.current) {
      clearTimeout(aiSilenceTimeoutRef.current);
      aiSilenceTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopRequestedRef.current = true;
      intentionalDisconnectRef.current = true;
      cancelPushToTalkEndSequence();

      if (recorderRef.current) {
        stopAllTracks(recorderRef.current);
        recorderRef.current = null;
      }

      if (playerRef.current) {
        closePlayerContext(playerRef.current);
        playerRef.current = null;
      }

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  const clearSessionError = () => {
    sessionErrorActiveRef.current = false;
  };

  const safeExit = async () => {
    sessionErrorActiveRef.current = false;
    shouldStopAIRef.current = true;
    try {
      if (
        playerRef.current &&
        typeof playerRef.current.interrupt === "function"
      ) {
        playerRef.current.interrupt();
      }
    } catch {
      // ignore
    }
    await stopSpeeking();
    onCloseRef.current?.();
  };

  const retrySession = async () => {
    setIsMicReady(false);

    if (isBrowserOffline()) {
      reportSessionError({
        ...OFFLINE_SESSION_ERROR,
        canFallbackAudioOnly: false,
      });
      return;
    }

    const errorCode = lastSessionErrorCodeRef.current;
    const forceFullReinit =
      errorCode === "disconnect" || errorCode === "connect_error";

    clearAiDialogueStartWatch();
    hasAIDialogueStartedRef.current = false;
    sessionErrorActiveRef.current = false;
    lastSessionErrorCodeRef.current = null;
    shouldStopAIRef.current = false;
    shouldPauseAIPlaybackRef.current = false;
    stopRequestedRef.current = false;
    intentionalDisconnectRef.current = false;
    pushToTalkHeldRef.current = false;
    isPushToTalkEndingRef.current = false;
    cancelPushToTalkEndSequence();

    // Soft retry keeps the same backend session. Full stopSpeeking() disconnects
    // the socket and the server ends the dialogue as "too short" (<10s).
    if (!forceFullReinit && socketRef.current?.connected) {
      try {
        if (
          playerRef.current &&
          typeof playerRef.current.interrupt === "function"
        ) {
          playerRef.current.interrupt();
        }
      } catch {
        // ignore
      }

      aiIsSpeakingRef.current = false;
      updateAiPlaybackActive(false);
      chunkQueue.current = [];
      isCurrentlySendingRef.current = false;
      voiceBargeInRef.current.reset();
      noiseAnalyzerRef.current.reset();

      if (aiSilenceTimeoutRef.current) {
        clearTimeout(aiSilenceTimeoutRef.current);
        aiSilenceTimeoutRef.current = null;
      }

      try {
        await ensureRecorderActive();
      } catch (e) {
        console.warn("ensureRecorderActive on soft retry failed:", e);
      }

      hasAIDialogueStartedRef.current = false;
      emitStartAiDialogue();
      setIsMicReady(true);
      return;
    }

    initialConnectInFlightRef.current = true;
    try {
      await stopSpeeking();
      stopRequestedRef.current = false;
      intentionalDisconnectRef.current = false;
      await initSpeeking();

      const connected = await waitForSocketConnected(socketRef.current);
      if (!connected || !socketRef.current) {
        reportSessionError({
          code: "connect_error",
          message: "Failed to reconnect. Check your connection and try again.",
          retryable: true,
          canFallbackAudioOnly: false,
        });
        return;
      }

      hasAIDialogueStartedRef.current = false;
      emitStartAiDialogue();
      setIsMicReady(true);
    } finally {
      initialConnectInFlightRef.current = false;
    }
  };

  const clearNoiseWarning = useCallback(() => {
    noiseAnalyzerRef.current.clearWarning();
  }, []);

  const startRoleplayDialogue = useCallback(async () => {
    setIsMicReady(false);
    initialConnectInFlightRef.current = true;
    try {
      await initSpeeking();

      const connected = await waitForSocketConnected(socketRef.current, 10_000);
      if (!connected || !socketRef.current) {
        reportSessionError({
          code: "connect_error",
          message: "Failed to connect. Check your connection and try again.",
          retryable: true,
          canFallbackAudioOnly: false,
        });
        return false;
      }

      // Brief beat so WavStreamPlayer is ready to receive the first AI audio.
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 300);
      });

      if (!socketRef.current?.connected) {
        const recovered = await attemptSilentReconnect();
        if (!recovered || !socketRef.current?.connected) {
          reportConnectionLostAfterFailedReconnect();
          return false;
        }
      }

      if (!hasAIDialogueStartedRef.current) {
        emitStartAiDialogue();
      }
      setIsMicReady(true);
      return true;
    } finally {
      initialConnectInFlightRef.current = false;
    }
  }, [
    emitStartAiDialogue,
    waitForSocketConnected,
    reportSessionError,
    attemptSilentReconnect,
    reportConnectionLostAfterFailedReconnect,
  ]);

  return {
    initSpeeking,
    startRoleplayDialogue,
    stopSpeeking,
    resumeSpeeking,
    pauseSpeeking,
    stopAISpeaking,
    retryInitSpeeking: initSpeeking,
    emitStartAiDialogue,
    safeExit,
    retrySession,
    clearSessionError,
    socketRef,
    hasAIDialogueStartedRef,
    isMicReady,
    aiIsSpeaking: aiIsSpeakingRef.current,
    aiPlaybackActive,
    playerRef,
    clearNoiseWarning,
    micMediaStream,
  };
};
